/**
 * present.js — what each kind of thread item IS, as data a renderer can lay out uniformly.
 *
 * Pure: takes an item, returns a descriptor. No React, no JSX, so the vocabulary can be asserted
 * directly and the renderers stay dumb.
 *
 * Two vocabularies meet in this widget and they are NOT the same one:
 *
 *  - the **meta-thread** tells a cell's story in FIVE records — `info`, `output`, `input_needed`,
 *    `input_provided`, `artifact`.
 *  - a **cell session** carries the worker's raw transcript — `text`, `thinking`, `tool_call`,
 *    `tool_result`, `status`, `turn_end`, `artifacts_published`, `policy_applied` and friends.
 *
 * **The type is shown as itself.** An earlier cut mapped every type through a table of English
 * titles, which meant a type the worker learned to post tomorrow rendered as a word nobody chose,
 * or as nothing. `metadata.type` is a stable, documented name; showing it costs one glance and
 * makes the surface extensible by default. Five of them is few enough that the renderer can afford
 * to give each a mark of its own as well — but the mark is a second signal over the name, never a
 * replacement for it, so a sixth type still arrives readable.
 *
 * **The verb is shown as itself too, for exactly the same reason.** Every record carries
 * `metadata.event` — `generated`, `uploaded`, `asked`, `answered`, and for `info` one of
 * `created | dispatched | looped | retried | resumed | route_failed | finished | steered`. It is
 * rendered verbatim. There is no per-type verb table here and there must not be one: a table is
 * what makes a record nobody planned for render as a word nobody chose. A record that arrives
 * without an event says nothing where the verb would go, which is the honest reading of a field
 * that is not there — the type alone still names it.
 *
 * **`metadata.label` is the value a record ENDED with, and it belongs to every type.** An agent's
 * exit (`ok`), the option a person picked at a gate (`approved`), the state a cell reached
 * (`completed`) — one optional field, absent on a record that ended with nothing worth naming. It
 * used to be two fields, `label` on the ones that route and `outcome` on a terminal `info`, which
 * put the same idea in two places and made `finished completed` read as two bare words in the head.
 * It is read the same way for every type, planned or not, and rendered in one place: a pill at the
 * end of the head.
 *
 * **`step` and `run` are structure, not prose.** They come off `metadata` as a `run` descriptor
 * — `{ step, run, key }`, keyed exactly as the object tree and the session pane name the same run —
 * and are rendered as one chip wherever they appear. They used to be spliced into the qualifier as
 * `"dispatched document (run 1)"`, which put the same two fields in two shapes on one line and left
 * a cell-level record with a blank where the phrase would have been. A record about no particular
 * run now carries no `run` at all, and the renderer shows nothing rather than an empty slot.
 *
 * **The body comes from `metadata`, not from `content[]`.** The worker writes `content[0].text` as
 * a one-line PREVIEW — `thinking` is cut at a few hundred characters, and a `tool_call` reads
 * `Bash → cat > /Users/…/architect` with the command chopped mid-path. The whole value is right
 * there in `metadata` (`text`, `tool.input`, `tool.output`), so that is what gets rendered. Nothing
 * on this surface is ever truncated by us: long bodies are CLAMPED by height and open in place.
 *
 * `tone` picks the visual treatment and is deliberately coarse — `plain`, `event` (muted), `good`,
 * `warn`. Status colour is carried by the pill vocabulary elsewhere; this is emphasis, not state.
 */

import { sessionKey } from './schema';

/**
 * Words that carry no meaning of their own in a record's sentence, so a body made only of these
 * plus the head word is the head word in a sentence.
 *
 * This used to be a LENGTH budget — twelve characters of residue — which measured the wrong thing.
 * A cell titled "Test" produced "Cell created: Test", left `celltest` once `created` was removed,
 * and vanished for being eight characters long, while the same record for a cell titled "What is
 * around?" survived at sixteen. Whether a title is worth reading has nothing to do with how long it
 * is.
 */
const GLUE = new Set(['cell', 'run', 'step', 'the', 'this', 'it']);

/**
 * True when the prose says nothing the head has not already said.
 *
 * The orchestrator writes both — `metadata.event: "created"` for machines and "Cell created" for
 * people — and rendering the pair stacks the same word twice. **The head always wins**: it is the
 * one line every item has, and an item whose type appears only when the prose happens not to
 * mention it is worse than a little repetition.
 *
 * So the BODY is what gets dropped, and only when it adds nothing: strike the head's own words out
 * of it, then the glue, and if NOTHING is left the prose was the head word in a sentence. "Cell
 * created: Test" survives `created` because `test` is left over, and a title is the part worth
 * reading however short it is; a bare "Cell created" leaves nothing and does not.
 */
function redundant(qualifier, body) {
  if (!qualifier || !body) return false;
  const words = (text) => String(text).toLowerCase().match(/[a-z0-9]+/g) || [];
  const head = new Set(words(qualifier));
  const rest = words(body).filter(word => !head.has(word) && !GLUE.has(word));
  return rest.length === 0;
}

/** A record's prose, from the content parts. */
export function bodyText(item) {
  return (Array.isArray(item && item.content) ? item.content : [])
    .filter(part => part && part.type === 'text' && part.text)
    .map(part => part.text)
    .join('\n');
}

/**
 * Which run a record is about, or null when it is about the cell as a whole.
 *
 * `key` is the run's name everywhere else in this widget — `<step>.<run>`, the same string the
 * object tree labels its rows with and the session pane heads itself with — so the chip in the feed
 * and the row in the tree read as the one run they are. A record carrying a `step` and no `run` is
 * about the step rather than an attempt at it, and names just the step.
 */
function runTag(metadata) {
  if (!metadata.step) return null;
  const numbered = metadata.run !== undefined && metadata.run !== null;
  return {
    step: metadata.step,
    run: numbered ? metadata.run : null,
    key: numbered ? sessionKey(metadata.step, metadata.run) : metadata.step
  };
}

/** JSON that reads as a block, for anything that is not already a string. */
function asText(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

/**
 * A tool call's input, in full.
 *
 * A shell command is the argument in the overwhelming majority of calls and it is what someone
 * reading a transcript is looking for, so `command` is lifted out of its wrapper and shown as the
 * command it is. Everything else keeps its shape as JSON — a `Write` call is a filename AND a body,
 * and flattening that would lose which was which.
 */
function toolInput(tool) {
  const input = tool && tool.input;
  if (!input) return null;
  if (typeof input === 'string') return input;
  if (typeof input.command === 'string') return input.command;
  return asText(input);
}

// The blocks a PERSON wrote, when they put a file among them. Only then: a record with prose alone
// renders through the path it always did, and an orchestrator record has nothing to position.
function positioned(item) {
  const content = (item && Array.isArray(item.content)) ? item.content : [];
  return content.some(part => part && 'file' === part.type) ? content : undefined;
}

/**
 * Assemble the common shape, dropping a body the head already carries.
 *
 * Two head slots, and they are not interchangeable. `event` is the record's own verb and belongs to
 * every meta record in the same place; `qualifier` is detail trailing the invariant part of the head,
 * and is all the transcript vocabulary has — a tool's name, a turn's status. No meta record sets one
 * any more, which is the point: their heads are uniform, and the vocabulary stays for the transcript.
 *
 * The body is tested against the WHOLE head, `run` included. "Dispatched work.1" repeats the verb
 * and the run chip, and testing only the verb left the run half of it on screen twice.
 */
function shape(type, fields) {
  const { event, qualifier, body, ...rest } = fields;
  const text = body && body.text;
  // `run` is the chip's shape, not its text — `key` is what a reader actually sees ("work.1").
  const head = [event || qualifier, rest.run && rest.run.key].filter(Boolean).join(' ');
  // A body with BLOCKS stays even when its text is empty: an attachment-only message has no prose, and
  // dropping it left the image a person sent absent from their own feed.
  const keep = body && (body.content || (text && !redundant(head, text)));
  return {
    type,
    event: event || null,
    qualifier: qualifier || null,
    body: keep ? body : null,
    label: null,
    tone: 'plain',
    ...rest
  };
}

/**
 * Describe a META-THREAD record — one of five, or one nobody has planned for yet.
 *
 * Every one of them wears the SAME head, in the same order: `type`, `event`, `run`, `label`. Nothing
 * about that varies by type — an `output` and an `info` differ in what hangs off the head, never in
 * the head itself. What each type adds under or after it:
 *
 * | type             | what it is                          | what it adds to the head         |
 * |------------------|-------------------------------------|----------------------------------|
 * | `info`           | the orchestrator's bookkeeping      | prose, as markdown               |
 * | `output`         | what an agent said when it finished | the run's cost; prose            |
 * | `input_needed`   | a question, as it was put           | the controls to answer it        |
 * | `input_provided` | the answer that unblocked it        | the answer itself, attributed    |
 * | `intent`         | what a person asked the cell for    | their words, if they wrote any   |
 * | `artifact`       | a file a run published              | the file, as something to open   |
 *
 * @returns {{ type, event, run, qualifier, label, tone, body, usage, alias, artifactPath, isSelf }}
 */
export function describeRecord(item) {
  const metadata = (item && item.metadata) || {};
  const type = metadata.type || 'info';
  const event = metadata.event || null;
  const prose = bodyText(item);
  const run = runTag(metadata);
  // Read for EVERY type, planned or not, for the same reason the type and the verb are: a table of
  // which types may end with a value is what makes a record nobody planned for lose the one word
  // that says how it came out.
  const label = metadata.label || null;

  /** The invariant head — `type`, `event`, `run`, `label` — with whatever the type hangs off it. */
  const record = (fields) => shape(type, { run, event, label, ...fields });

  if (type === 'output') {
    // What the agent said at the end of a run, and the exit that routed on it. A null `label` is
    // not a missing field: the run's exit could not be parsed, which is a state worth naming.
    return record({
      unrouted: metadata.label === null || metadata.label === undefined,
      usage: metadata.usage || null,
      body: { mode: 'markdown', text: prose }
    });
  }
  if (type === 'input_needed') {
    // The question as it was put, plus the REQUEST it was raised from. The request is what decides
    // whether an answer can be composed at all — its kind, its options, the policy that stopped a
    // call — so it travels with the record rather than being re-derived from the prose.
    const request = metadata.request || null;
    // The head says `asked`, and stops there. The request's `kind` used to trail it, but a kind is
    // what the CONTROLS are made of, not something to read: the answer it shapes is already sitting
    // underneath as buttons or a text box, so naming it spent a word of the head on a fact the next
    // line demonstrates. It stays on `request` for whoever composes those controls.
    return record({ request, body: { mode: 'text', text: prose } });
  }
  if (type === 'input_provided') {
    // `source` decides the side, not the channel: `external` is the actor's own words, posted for
    // them, and belongs where a person's messages go. A `system` one is the orchestrator recording
    // the verdict the worker's prompt bridge read back — which on an approval is `approve` or
    // `deny` whatever the person typed, so it is genuinely not their words and does not sit there.
    // Markdown, with `breaks`, because it is what a PERSON typed: they wrote it in a field where
    // Shift+Enter is a newline, so their fences, lists and line breaks are all deliberate. Rendering
    // it flat printed the backticks and ran their lines together.
    return record({
      tone: 'good',
      isSelf: metadata.source === 'external',
      body: {
        mode: 'markdown',
        breaks: true,
        text: prose,
        // Gated on `source`, unlike `intent`: a client writes an intent by definition, so none is
        // orchestrator-authored (blobhub-app-missions/CLAUDE.md:55). An answer can be either.
        content: metadata.source === 'external' ? positioned(item) : undefined
      }
    });
  }
  if (type === 'artifact') {
    // `path` is the run-relative name and the alias is the address. The split is the fallback for
    // a record written before `path` existed; the chip needs a name either way.
    const alias = metadata.alias || '';
    const fromAlias = alias.includes('/artifacts/') ? alias.split('/artifacts/')[1] : alias;
    return record({
      alias,
      artifactPath: metadata.path || fromAlias,
      contentType: metadata.content_type || null,
      size: metadata.size,
      body: null
    });
  }
  if (type === 'info') {
    return record({
      // The exception worth lifting out of the muted stream: a cell reaching `completed` or
      // `failed` is what a person scans its history FOR. The two inputs are NOT one field wearing
      // two names — `completed` and `failed` are how the cell came out and arrive as the `label`,
      // while `route_failed` is what the orchestrator DID and arrives as the verb.
      tone: label === 'failed' || event === 'route_failed' ? 'warn'
        : label === 'completed' ? 'good'
        : 'event',
      // Markdown, like an `output` and through the same renderer: the orchestrator writes a cell's
      // own title into `created` and its closing summary into `finished`, and both are prose people
      // wrote. A line of pure bookkeeping is dropped by `redundant` above and never reaches this.
      body: { mode: 'markdown', text: prose }
    });
  }
  if (type === 'intent') {
    // What a person ASKED FOR, which is a different thing from what happened. An `info` is the
    // orchestrator saying it did something; this is somebody requesting it, and the two are kept
    // apart precisely so a reader can tell a request that is still pending from one that landed.
    //
    // On the person's side, like their answers, because it is the same kind of thing: their own
    // words, posted by them. The verb is in the head — `intent · pause` — so a request with nothing
    // typed against it is a head and no body, which is the honest rendering of a bare button press.
    return record({ tone: 'event', isSelf: true, body: {
      mode: 'markdown', breaks: true, text: prose, content: positioned(item)
    } });
  }
  // A type nobody has planned for still renders, as itself and with whatever it says about its own
  // run, its own verb and the value it ended with — showing a name that is merely unfamiliar beats
  // silently dropping the record.
  return record({ tone: 'event', body: { mode: 'text', text: prose } });
}

/**
 * Describe a CELL SESSION transcript item.
 * @returns {{ type, qualifier, tone, body, stats, aliases, rules, truncated }}
 */
export function describeItem(item) {
  const metadata = (item && item.metadata) || {};
  const type = metadata.type || 'text';
  const prose = bodyText(item);
  // The worker excerpts an item that would exceed the thread's size budget, and records that it
  // did. Saying so is the difference between a short transcript and a transcript with a hole.
  const truncated = metadata.truncated ? (metadata.truncated_fields || []) : null;

  if (type === 'turn_end') {
    const ok = metadata.status === 'completed';
    // The run boundary. The worker's body text for it is a synthetic restatement of the status,
    // so the status carries it alone.
    return shape(type, {
      qualifier: metadata.status || 'ended',
      tone: ok ? 'good' : 'warn',
      stats: metadata.stats || null,
      error: metadata.error || null,
      truncated,
      body: null
    });
  }
  if (type === 'tool_call') {
    const tool = metadata.tool || {};
    return shape(type, {
      qualifier: tool.name || null,
      tone: 'event',
      truncated,
      body: { mode: 'code', text: toolInput(tool) }
    });
  }
  if (type === 'tool_result') {
    const tool = metadata.tool || {};
    return shape(type, {
      qualifier: tool.is_error ? 'error' : null,
      tone: tool.is_error ? 'warn' : 'event',
      truncated,
      // `output` is the whole result; `content[0].text` is the worker's one-line summary of it.
      body: { mode: 'code', text: asText(tool.output) || prose }
    });
  }
  if (type === 'artifacts_published') {
    const counts = metadata.counts || {};
    return shape(type, {
      qualifier: `${counts.published || 0} published`
        + (counts.skipped ? `, ${counts.skipped} skipped` : ''),
      tone: metadata.error ? 'warn' : 'event',
      aliases: metadata.aliases || [],
      skipped: metadata.skipped || [],
      error: metadata.error || null,
      truncated,
      body: null
    });
  }
  if (type === 'policy_applied') {
    // The ack, not decoration: a guarded run without one was executed unenforced, and the
    // orchestrator fails it.
    const rules = metadata.rules || [];
    return shape(type, {
      qualifier: `${rules.length} rule(s)`,
      tone: 'event',
      rules,
      policyDefault: metadata.default || null,
      truncated,
      body: null
    });
  }
  if (type === 'policy_denied') {
    return shape(type, {
      qualifier: metadata.rule || null,
      tone: 'warn',
      effect: metadata.effect || null,
      truncated,
      body: { mode: 'text', text: prose }
    });
  }
  if (type === 'status') {
    return shape(type, {
      qualifier: metadata.status || null,
      tone: metadata.status === 'error' ? 'warn' : 'event',
      truncated,
      body: { mode: 'text', text: metadata.detail || prose }
    });
  }
  if (type === 'thinking') {
    // `metadata.text` is the whole thought; the content line is its first few hundred characters.
    return shape(type, {
      tone: 'event',
      truncated,
      body: { mode: 'text', text: metadata.text || prose }
    });
  }
  if (type === 'context' || type === 'steering') {
    return shape(type, {
      tone: type === 'context' ? 'event' : 'plain',
      isSelf: type === 'steering',
      truncated,
      body: { mode: 'markdown', text: prose }
    });
  }
  if (type === 'text') {
    return shape(type, { truncated, body: { mode: 'markdown', text: prose } });
  }
  // An unknown type is shown as itself rather than hidden — a transcript that silently drops an
  // item the worker learned to post is worse than one that shows a name nobody recognises yet.
  return shape(type, { tone: 'event', truncated, body: { mode: 'text', text: prose } });
}

/** Tokens in, human out. `input_tokens` already INCLUDES the cached portion; never add them. */
export function formatUsage(usage) {
  if (!usage) return null;
  const parts = [`${usage.input_tokens || 0} in`];
  if (usage.input_tokens_cached) parts.push(`${usage.input_tokens_cached} cached`);
  parts.push(`${usage.output_tokens || 0} out`);
  if (usage.duration_ms) parts.push(`${Math.round(usage.duration_ms / 1000)}s`);
  return parts.join(' · ');
}

/** Bytes in, human out. */
export function formatSize(size) {
  if (size === undefined || size === null) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
